import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AccountSelector } from '@/components/validation/account-selector';
import { createBookingFromTransaction } from '@/services/bookingService';
import { toast } from 'sonner';

interface Transaction {
  id: string;
  date: string;
  amount: number;
  counterparty: string;
  confidence: number;
  debitAccount: string;
  creditAccount: string;
  vatRate: number;
  description?: string;
}

interface FocusedModeProps {
  transactions: Transaction[];
  onExit: () => void;
  onComplete: () => void;
}

export const FocusedMode: React.FC<FocusedModeProps> = ({ 
  transactions, 
  onExit, 
  onComplete 
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editingField, setEditingField] = useState<'debit' | 'credit' | null>(null);
  const [processedCount, setProcessedCount] = useState(0);
  const [startTime] = useState(Date.now());
  const [isProcessing, setIsProcessing] = useState(false);
  
  const currentTransaction = transactions[currentIndex];
  const progress = (currentIndex / transactions.length) * 100;
  
  const handleApproveAndBook = useCallback(async () => {
    if (isProcessing) return;
    
    setIsProcessing(true);
    try {
      // Create journal entry
      await createBookingFromTransaction(currentTransaction.id);
      
      setProcessedCount(prev => prev + 1);
      
      toast.success('Gebucht', {
        description: `${currentTransaction.counterparty} - CHF ${currentTransaction.amount.toFixed(2)}`
      });
      
      // Auto-advance to next
      if (currentIndex < transactions.length - 1) {
        setCurrentIndex(prev => prev + 1);
      } else {
        // Show completion summary
        const duration = Math.floor((Date.now() - startTime) / 1000);
        const minutes = Math.floor(duration / 60);
        const seconds = duration % 60;
        toast.success('Verarbeitung abgeschlossen!', {
          description: `${processedCount + 1} Transaktionen in ${minutes}:${seconds.toString().padStart(2, '0')} min`
        });
        onComplete();
      }
    } catch (error) {
      toast.error('Buchung fehlgeschlagen', {
        description: error instanceof Error ? error.message : 'Unbekannter Fehler'
      });
    } finally {
      setIsProcessing(false);
    }
  }, [currentTransaction, currentIndex, transactions.length, processedCount, startTime, isProcessing]);
  
  const handleReject = useCallback(() => {
    // Mark for review
    toast.info('Zurückgestellt');
    
    // Auto-advance
    if (currentIndex < transactions.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      onComplete();
    }
  }, [currentIndex, transactions.length]);
  
  const handleSkip = useCallback(() => {
    if (currentIndex < transactions.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      onComplete();
    }
  }, [currentIndex, transactions.length]);
  
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Don't trigger if in input field
      if (e.target instanceof HTMLInputElement) return;
      
      switch (e.key.toLowerCase()) {
        case 'enter':
          e.preventDefault();
          if (!isEditing) handleApproveAndBook();
          break;
        case 'e':
          e.preventDefault();
          if (!isEditing) {
            setIsEditing(true);
            setEditingField('debit');
          } else if (editingField === 'debit') {
            setEditingField('credit');
          } else {
            setIsEditing(false);
            setEditingField(null);
          }
          break;
        case 'r':
          e.preventDefault();
          handleReject();
          break;
        case 's':
          e.preventDefault();
          handleSkip();
          break;
        case 'escape':
          e.preventDefault();
          if (isEditing) {
            setIsEditing(false);
            setEditingField(null);
          } else {
            onExit();
          }
          break;
        case '?':
          e.preventDefault();
          // Show full shortcuts dialog
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isEditing, handleApproveAndBook, handleReject, handleSkip, onExit]);
  
  if (!currentTransaction) return null;
  
  return (
    <div className="fixed inset-0 bg-brand-background z-50 flex flex-col">
      {/* Header with progress */}
      <div className="bg-white border-b border-brand-border p-4">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold">
            🎯 Focused Mode - Schnellverarbeitung
          </h2>
          <button 
            onClick={onExit}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            ESC zum Beenden
          </button>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-600">
            Transaktion {currentIndex + 1} von {transactions.length}
          </span>
          <Progress value={progress} className="flex-1" />
          <span className="text-sm font-medium">{Math.round(progress)}%</span>
        </div>
      </div>
      
      {/* Main transaction display */}
      <div className="flex-1 flex items-center justify-center p-6">
        <Card className="max-w-3xl w-full p-6 space-y-4">
          {/* Transaction details */}
          <div className="grid grid-cols-2 gap-4 text-lg">
            <div>
              <div className="text-sm text-gray-600">Datum</div>
              <div className="font-medium">{currentTransaction.date}</div>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-600">Betrag</div>
              <div className="text-2xl font-bold text-brand-primary">
                CHF {currentTransaction.amount.toFixed(2)}
              </div>
            </div>
          </div>
          
          <div>
            <div className="text-sm text-gray-600 mb-1">Gegenpartei</div>
            <div className="text-xl font-medium">
              {currentTransaction.counterparty || currentTransaction.description || 'Unbekannt'}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="text-sm text-gray-600">KI Confidence:</div>
            <Badge variant="default" className="text-sm">
              {currentTransaction.confidence}%
            </Badge>
          </div>
          
          {/* Proposed booking */}
          <div className="border border-brand-border rounded-lg p-4 bg-gray-50">
            <div className="text-sm font-medium mb-3">Vorgeschlagene Buchung:</div>
            <div className="space-y-2 font-mono text-sm">
              <div className="flex justify-between">
                <span>Soll: {isEditing && editingField === 'debit' ? (
                  <AccountSelector 
                    value={currentTransaction.debitAccount}
                    onChange={(account) => {
                      // Update transaction logic here
                      setIsEditing(false);
                      setEditingField(null);
                    }}
                    placeholder="Debit Account"
                  />
                ) : currentTransaction.debitAccount}</span>
                <span>CHF {currentTransaction.amount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>Haben: {isEditing && editingField === 'credit' ? (
                  <AccountSelector 
                    value={currentTransaction.creditAccount}
                    onChange={(account) => {
                      // Update transaction logic here
                      setIsEditing(false);
                      setEditingField(null);
                    }}
                    placeholder="Credit Account"
                  />
                ) : currentTransaction.creditAccount}</span>
                <span>CHF {currentTransaction.amount.toFixed(2)}</span>
              </div>
              <div>MwSt: {currentTransaction.vatRate}%</div>
            </div>
          </div>
          
          {/* AI recommendation */}
          <div className="text-sm text-gray-600 italic">
            🧠 KI-Empfehlung: Basiert auf ähnlichen Buchungen
          </div>
          
          {/* Keyboard shortcuts guide */}
          <div className="border border-blue-200 bg-blue-50 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2">
                <kbd className="px-2 py-1 bg-white border border-gray-300 rounded font-mono text-xs">
                  ENTER
                </kbd>
                <span>Buchen & Weiter</span>
              </div>
              <div className="flex items-center gap-2">
                <kbd className="px-2 py-1 bg-white border border-gray-300 rounded font-mono text-xs">
                  E
                </kbd>
                <span>Konto ändern</span>
              </div>
              <div className="flex items-center gap-2">
                <kbd className="px-2 py-1 bg-white border border-gray-300 rounded font-mono text-xs">
                  R
                </kbd>
                <span>Zurückstellen</span>
              </div>
              <div className="flex items-center gap-2">
                <kbd className="px-2 py-1 bg-white border border-gray-300 rounded font-mono text-xs">
                  S
                </kbd>
                <span>Überspringen</span>
              </div>
            </div>
          </div>
          
          {/* Next transaction preview */}
          {currentIndex < transactions.length - 1 && (
            <div className="text-sm text-gray-600 pt-4 border-t border-gray-200">
              <span className="font-medium">Nächste:</span>{' '}
              {transactions[currentIndex + 1].counterparty} 
              (CHF {transactions[currentIndex + 1].amount.toFixed(2)}) →
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};
